// Reads the academy's Batches folder from OneDrive through Microsoft Graph (admin sync only).
import { buildBatchPayload, shareId } from './library-parse.js';
import { AppError } from './api.js';

const GRAPH = 'https://graph.microsoft.com/v1.0';
const ITEM_FIELDS = 'id,name,webUrl,folder,file,size,video,sharepointIds,parentReference';

async function graph(token, path) {
  let res;
  try {
    res = await fetch(path.startsWith('https://') ? path : GRAPH + path, { headers: { Authorization: `Bearer ${token}` } });
  } catch (e) {
    throw new AppError('Could not reach Microsoft. Check your connection and try again.', 'offline');
  }
  if (res.status === 401) throw new AppError('Microsoft asked to sign in again before reading OneDrive.', 'graph_auth', 401);
  if (res.status === 403) throw new AppError('Your Microsoft account can’t open that folder. Use the account that owns or shares the Batches folder.', 'graph_forbidden', 403);
  if (res.status === 404) throw new AppError('That folder link didn’t work. Copy the link to the Batches folder again.', 'graph_not_found', 404);
  if (res.status === 429 || res.status === 503) {
    const wait = Math.min(Number(res.headers.get('Retry-After') || 2), 20);
    await new Promise((r) => setTimeout(r, wait * 1000));
    return graph(token, path);
  }
  if (!res.ok) throw new AppError(`Microsoft returned an error (${res.status}). Please try again.`, 'graph_error', res.status);
  return res.json();
}

// From a OneDrive sharing link (or the address in the browser bar) to the folder's ids.
export async function resolveFolder(token, link) {
  const text = String(link || '').trim();
  if (!/^https:\/\/[^/]+\.sharepoint\.com\//i.test(text)) {
    throw new AppError('Paste a link that starts with https:// and points to your OneDrive or SharePoint.', 'bad_link');
  }
  let url = text;
  try {
    // The address bar of OneDrive on the web uses ?id=/personal/.../Documents/Batches
    const u = new URL(text);
    const id = u.searchParams.get('id');
    if (id && /^\/(personal|sites)\//.test(id)) {
      const seg = id.split('/');
      const sitePath = seg.slice(0, 3).map(encodeURIComponent).join('/');
      const itemPath = seg.slice(4).map(encodeURIComponent).join('/');
      const item = await graph(token, `/sites/${u.hostname}:${sitePath}:/drive/root:/${itemPath}?$select=${ITEM_FIELDS}`);
      return toRoot(item);
    }
  } catch (e) {
    if (e instanceof AppError && e.reason !== 'graph_not_found') throw e;
  }
  const item = await graph(token, `/shares/${shareId(url)}/driveItem?$select=${ITEM_FIELDS}`);
  return toRoot(item);
}

function toRoot(item) {
  if (!item.folder) throw new AppError('That link points to a file. Copy the link to the Batches folder itself.', 'not_folder');
  return { drive_id: item.parentReference && item.parentReference.driveId, item_id: item.id, name: item.name, web_url: item.webUrl };
}

export async function listChildren(token, driveId, itemId) {
  const out = [];
  let next = `/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/children?$select=${ITEM_FIELDS}&$top=200`;
  while (next) {
    const page = await graph(token, next);
    out.push(...(page.value || []));
    next = page['@odata.nextLink'] || null;
  }
  return out;
}

async function pool(items, size, work) {
  let i = 0;
  const runners = Array.from({ length: Math.min(size, items.length) }, async () => {
    while (i < items.length) {
      const item = items[i++];
      await work(item);
    }
  });
  await Promise.all(runners);
}

// Read every batch folder and save it, one batch at a time, reporting progress.
export async function syncLibrary(token, root, rpc, onProgress = () => {}) {
  onProgress({ phase: 'listing', done: 0, total: 0 });
  const top = await listChildren(token, root.drive_id, root.item_id);
  const batches = top.filter((x) => x.folder).sort((a, b) => a.name.localeCompare(b.name));
  const stats = { batches: 0, classes: 0, new_classes: 0, files: 0 };
  for (let n = 0; n < batches.length; n++) {
    const b = batches[n];
    onProgress({ phase: 'batch', done: n, total: batches.length, name: b.name });
    const children = await listChildren(token, root.drive_id, b.id);
    const inside = new Map();
    await pool(children.filter((x) => x.folder), 4, async (c) => {
      inside.set(c.id, await listChildren(token, root.drive_id, c.id));
    });
    const res = await rpc('admin_sync_batch', { p_batch: buildBatchPayload(b, root.drive_id, children, inside) });
    stats.batches += 1;
    stats.classes += res.classes;
    stats.new_classes += res.new_classes;
    stats.files += res.files;
  }
  onProgress({ phase: 'finishing', done: batches.length, total: batches.length });
  const lastSync = await rpc('admin_sync_finish', { p_root: root, p_seen_batch_items: batches.map((b) => b.id), p_stats: stats });
  return { stats, lastSync };
}
